import { SEO } from "@/components/SEO";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Camera, ArrowRight, ChevronLeft, ChevronRight, X, Phone } from "lucide-react";
import { BUSINESS } from "@/data/businessInfo";
import { ServiceCardsSection, LocationCardsSection } from "@/components/SharedSections";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function GalleryPage() {
  const [lightbox, setLightbox] = useState(null);
  const photos = BUSINESS.gallery;

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prevPhoto = () => setLightbox(i => (i - 1 + photos.length) % photos.length);
  const nextPhoto = () => setLightbox(i => (i + 1) % photos.length);

  return (
    <div>
      <SEO title="Project Gallery - Real Electrical Work | Southern Light Solutions" description="Browse real electrical projects completed by Southern Light Solutions across Port St. Lucie and the Treasure Coast. Generator maintenance, panel upgrades, EV chargers, meter replacements, and more." path="/gallery" />

      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Work</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Project Gallery</h1>
            <p className="mt-4 text-base lg:text-lg text-[#6B3A1F] max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>Every photo is from a real Southern Light Solutions project across Port St. Lucie and the Treasure Coast. Generator maintenance, electrical panel work, EV charger installations, emergency repairs, and more.</p>
            <p className="mt-2 text-sm text-[#6B3A1F]/60" style={{ fontFamily: 'Manrope, sans-serif' }}>{photos.length} project photos — click any image to view full size</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {photos.map((img, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.04 }}>
                <button onClick={() => openLightbox(i)} className="relative group rounded-2xl overflow-hidden aspect-square border border-[#6B3A1F]/10 shadow-sm hover:shadow-xl transition-shadow duration-300 w-full block cursor-pointer" data-testid={`gallery-photo-${i}`}>
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-center" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#401303]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <span className="text-white text-sm font-bold translate-y-2 group-hover:translate-y-0 transition-transform duration-300" style={{ fontFamily: 'Outfit, sans-serif' }}>{img.caption}</span>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 bg-white rounded-2xl border border-[#6B3A1F]/10 p-8 lg:p-10 text-center">
            <Camera className="w-10 h-10 text-[#D97904] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#401303] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Want to See Your Project Here?</h2>
            <p className="text-sm text-[#6B3A1F] mb-6 max-w-md mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>Every project we complete is a reflection of our commitment to quality. Let us add yours to the gallery.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 px-7 py-3.5 btn-gradient rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="gallery-book-btn">
                Request an Estimate <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-[#401303] text-[#401303] font-bold rounded-full text-sm hover:bg-[#401303] hover:text-white transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <ServiceCardsSection title="Services We Provide" />
      <LocationCardsSection title="Areas We Serve" />

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] bg-[#401303]/95 flex items-center justify-center" onClick={closeLightbox} data-testid="gallery-lightbox">
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 p-2 text-white/70 hover:text-white z-10" aria-label="Close">
            <X className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prevPhoto(); }} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors duration-200 z-10" aria-label="Previous">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); nextPhoto(); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors duration-200 z-10" aria-label="Next">
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[85vh] px-4" onClick={e => e.stopPropagation()}>
            <img src={photos[lightbox].src} alt={photos[lightbox].alt} className="max-w-full max-h-[75vh] object-contain mx-auto rounded-lg" />
            <div className="text-center mt-4">
              <p className="text-white text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{photos[lightbox].caption}</p>
              <p className="text-white/50 text-sm mt-1">{lightbox + 1} of {photos.length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
